import React, { useState, useEffect, useContext } from 'react';
import { Card, Progress, Button, Typography, Row, Col, Table, Spin } from 'antd';
import { Tooltip as AntdTooltip } from 'antd';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { useLocation, useOutletContext } from 'react-router-dom';

import styles from './CostOptimization.module.css';
import dollarImg from '../../../../assets/images/dollar-symbol.svg';
import piggyImg from '../../../../assets/images/piggy-bank.svg';
import speedImg from '../../../../assets/images/speedometer.svg';
import api from '../../../../api';
import { CustomModal } from '../../../Dashboard/CustomComponents/PageLoader.style';
import { BreadcrumbContext } from '../../../../context/breadcrumbContext';
import is from 'date-fns/esm/locale/is/index.js';
import noDataImg from '@iso/assets/images/no-data-found.svg';


const { Title, Text } = Typography;



export default function CostOptimization() {
  const { overviewData } = useOutletContext();
  const [totalCost, setTotalCost] = useState(null);
  const [costComponents, setCostComponents] = useState(null);

  const [expandedKeys, setExpandedKeys] = useState([]);
  // const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [recommended, setRecommended] = useState([]);
  const [idealNodes, setIdealNodes] = useState([]);
  const [unusedPVCs, setUnusedPVCs] = useState([]);
  const [wastedResource, setWastedResource] = useState(null);
  const [loadingSpend, setLoadingSpend] = useState(false);
  const [loadingWasted, setLoadingWasted] = useState(false);
  const [isResizingLoading, setIsResizingLoading] = useState(false);
  const [isIdleNodesLoading, setIsIdleNodesLoading] = useState(false);
  const [isPVCsLoading, setIsPVCsLoading] = useState(false);
  const [isPrometheusMissing, setIsPrometheusMissing] = useState(false);
  // const [costTrendData, setCostTrendData ] = useState([]);
  const [costTrendData, setCostTrendData] = useState([]);
  // { date: "2025-04-15", value: 10.2 },
  // { date: "2025-04-16", value: 9.8 },
  // { date: "2025-04-17", value: 10.1 },
  // { date: "2025-04-18", value: 11.3 },
  // { date: "2025-04-19", value: 9.2 },
  // { date: "2025-04-20", value: 10.6 },
  // { date: "2025-04-21", value: 10.4 },
  // { date: "2025-04-22", value: 9.5 },
  // { date: "2025-04-23", value: 11.0 },
  // { date: "2025-04-24", value: 9.7 },
  // { date: "2025-04-25", value: 10.3 },
  // { date: "2025-04-26", value: 10.8 },
  // { date: "2025-04-27", value: 9.1 },
  // { date: "2025-04-28", value: 10.0 },
  // { date: "2025-04-29", value: 10.5 },
  // { date: "2025-04-30", value: 8.9 },
  // { date: "2025-05-01", value: 10.7 },
  // { date: "2025-05-02", value: 10.9 },
  // { date: "2025-05-03", value: 9.6 },
  // { date: "2025-05-04", value: 11.1 },
  // { date: "2025-05-05", value: 10.0 },
  // { date: "2025-05-06", value: 9.3 },
  // { date: "2025-05-07", value: 10.4 },
  // { date: "2025-05-08", value: 9.8 },
  // { date: "2025-05-09", value: 10.6 },
  // { date: "2025-05-10", value: 10.2 },
  // { date: "2025-05-11", value: 10.3 },
  // { date: "2025-05-12", value: 9.0 },
  // { date: "2025-05-13", value: 10.8 },
  // { date: "2025-05-14", value: 10.2 },

  const fetchCostTrend = async () => {
    const clusterUniqueId = location.pathname.split("/").slice(-2)[0];

    try {
      const res = await api.get(`/api/cluster/cost-trend/${clusterUniqueId}/`);
      if (res.data?.status) {
        const rawData = res.data.data.data || {};

        const transformed = Object.entries(rawData).map(([date, value]) => ({
          date,
          value,
        }));

        setCostTrendData(transformed);
      }
      else {
        const errMsg = res.data.message;
        if (errMsg === "Prometheus not present in cluster") {
          setIsPrometheusMissing(true);
        }
      }
    } catch (err) {
      console.error("Cost trend API error", err);
    }
  };

  const fetchPodRecommendation = async () => {
    const clusterUniqueId = location.pathname.split("/").slice(-2)[0];
    setIsResizingLoading(true);
    try {
      const res = await api.get(`/api/cluster/recommended-nodes/${clusterUniqueId}/`);
      if (res.data?.status) {
        console.log("res.status:", res.data?.status)
        const rawData = res.data.data.clubbed || {};
        setRecommended(rawData);
      }
      else {
        const errMsg = res.data.message;
        if (errMsg === "Prometheus not present in cluster") {
          setIsPrometheusMissing(true);
        }
      }
    } catch (err) {
      console.log("err:", err.response)
      const errMsg = err?.response?.data?.message;
      if (errMsg === "Prometheus not present in cluster") {
        setIsPrometheusMissing(true);
      }
      console.error("Recommended Pods error", err);
    } finally {
      setIsResizingLoading(false);
    }
  };

  const fetchWastedResources = async () => {
    setLoadingWasted(true);
    const clusterUniqueId = location.pathname.split("/").slice(-2)[0];

    try {
      const res = await api.get(`/api/cluster/wasted-resource/${clusterUniqueId}/`);
      const rawData = res.data.data;

      setWastedResource(rawData);
    } catch (err) {
      console.error("Wasted Resource fetching error", err);
    }
    finally {
      setLoadingWasted(false);
    }
  };

  const fetchIdealNodes = async () => {
    setIsIdleNodesLoading(true);
    const clusterUniqueId = location.pathname.split("/").slice(-2)[0];

    try {
      const res = await api.get(`/api/cluster/ideal-nodes/${clusterUniqueId}/`);

      setIdealNodes(res.data.data || []);
    } catch (err) {
      console.error("Ideal Nodes fetching error", err);
    }
    finally {
      setIsIdleNodesLoading(false);
    }
  };


  const fetchUnusedPVCs = async () => {
    const clusterUniqueId = location.pathname.split("/").slice(-2)[0];
    // setLoadingWasted(true);
    setIsPVCsLoading(true);

    try {
      const [pvcRes] = await Promise.all([
        // api.get(`/api/cluster/recommended-nodes/${clusterUniqueId}/`),
        api.get(`/api/cluster/unused-pvc/${clusterUniqueId}/`),

      ]);

      // setRecommended(recRes.data.data?.results || []);
      setUnusedPVCs(pvcRes.data.data || []);

    } catch (err) {
      console.error("Optimization API error", err);
    } finally {
      setIsPVCsLoading(false);
    }
  };

  useEffect(() => {
    fetchUnusedPVCs();
    fetchPodRecommendation();
    fetchWastedResources();
    fetchIdealNodes();
    fetchCostTrend();

  }, []);
  const gradientStrokeColor = {
    '0%': '#6486EE',  // Start color
    '100%': '#9747FF', // End color
  };

  const groupedWorkloads = recommended.reduce((acc, item) => {
    const key = item.Workload;
    if (!acc[key]) {
      acc[key] = {
        name: key,
        pod_name: item.Workload,
        containers: []
      };
    }

    acc[key].containers.push({
      container: item.Container,
      cpu: {
        current: item.cpu_request,
        recommended: item.CPU_REQUEST_RECOMMENDED,
      },
      memory: {
        current: item.memory_request,
        recommended: item.MEMORY_REQUEST_RECOMMENDED,
      },
      risk: "High"
    });

    return acc;
  }, {});

  const optimizationGroups = [
    {
      key: 0,
      title: 'Resize overprovisioned workloads',
      // namespace: "payment",
      savings: 0, // calculate later
      workloads: Object.values(groupedWorkloads)
    },
    {
      key: 1,
      title: 'Remove idle nodes (no pods scheduled)',
      savings: 0,
      workloads: idealNodes?.map((node, i) => ({
        key: i,
        name: node.name,
        cpuUsage: node.usage.cpu,
        memoryUsage: node.usage.memory,
        allocatablecpu: node.allocatable.cpu,
        allocatablememory: node.allocatable.memory,

        savings: (node.hourly_cost_usd?.[0] || 0).toFixed(2)
      }))
      // workloads: [],
    },
    // {
    //   key: 2,
    //   title: 'Enable Cluster Autoscalar',
    //   savings: 0,
    //   workloads: idealNodes.map((node, i) => ({
    //     key: i,
    //     name: node.name,
    //     cpu: 0,
    //     memory: 0,
    //     savings: (node.hourly_cost_usd?.[0] || 0).toFixed(2)
    //   }))
    // },
    {
      key: 2,
      title: 'Delete Unused PVCs',
      savings: 0,
      workloads: unusedPVCs?.map((pvc, i) => ({
        key: i,
        name: pvc.name,
        cpu: 0,
        memory: pvc.size_gi,
        savings: pvc.cost_usd?.toFixed(2)
      }))
    },
  ];

  let { pageHeading, setPageHeading, pageHeadingsObj, setPageHeadingsObj, breadcrumbObj, breadData, setBreadData } = useContext(BreadcrumbContext);
  const fetchTotalCost = async () => {
    setLoadingSpend(true);
    const clusterUniqueId = location.pathname.split("/").slice(-2)[0];
    try {
      const res = await api.get(`/api/cluster/cluster-cost/${clusterUniqueId}/`);
      const data = res.data;
      if (data.status) {
        setTotalCost(data.data.grand_total);
        const { grand_total, nodes, ...components } = data.data;
        setCostComponents(components);
      } else {
        setTotalCost("N/A");
      }
    } catch (err) {
      setTotalCost("N/A");
    } finally {
      setLoadingSpend(false);
    }
  };

  useEffect(() => {
    fetchTotalCost();
  }, []);

  useEffect(() => {
    // setSideCollapsed(true);
    // setSideOpenDrawer(false);
    // setIsStill(false);
    setPageHeading({
      title: pageHeadingsObj.clusters.clusterOverview.CostOptimization.title,
      subTitle: pageHeadingsObj.clusters.clusterOverview.CostOptimization.subTitle,
    });
  }, []);



  const summaryValue = totalCost !== null ? `$${Number(totalCost).toFixed(2).toLocaleString()}/mo` : 'Loading...';

  const efficiency = wastedResource
    ? 100 - ((wastedResource.totals.wasted_cpu_percent + wastedResource.totals.wasted_memory_percent) / 2)
    : 0;

  const nestedColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
    },
    {
      title: 'CPU',
      dataIndex: 'cpu',
      key: 'cpu',
      width: 200,
      render: (_, row) => {
        const usedCPU = row.cpuUsage || "";
        const allocatableCPU = row.allocatablecpu || "";

        let cpuUsage = null;
        let usageValueDisplay = "-";

        if (usedCPU && allocatableCPU) {
          let usedCores = 0;

          if (usedCPU.includes("n")) {
            usedCores = parseInt(usedCPU.replace("n", ""), 10) / 1e9;
          } else if (usedCPU.includes("m")) {
            usedCores = parseInt(usedCPU.replace("m", ""), 10) / 1000;
          } else {
            usedCores = parseFloat(usedCPU) || 0;
          }

          const allocatableCores = parseInt(allocatableCPU.replace("m", ""), 10) / 1000;
          const usagePercent = allocatableCores > 0 ? (usedCores / allocatableCores) * 100 : 0;

          cpuUsage = usagePercent.toFixed(0);
          usageValueDisplay = `${usedCores.toFixed(1)}C / ${allocatableCores.toFixed(1)}C`;
        }

        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Progress
              percent={cpuUsage ? parseFloat(cpuUsage) : 0}
              size="small"
              strokeColor={gradientStrokeColor}
              status="active"
              showInfo
              format={p => `${p}%`}
              strokeWidth={15}
            />
            <span style={{ fontSize: "11px", color: "#A8B0B9" }}>{usageValueDisplay}</span>
          </div>
        );
      }
    },
    {
      title: 'Memory',
      dataIndex: 'memory',
      key: 'memory',
      width: 200,
      render: (_, row) => {
        console.log("row: ", row)
        const usedMem = row.memoryUsage || "";
        const allocMem = row.allocatablememory || "";
        console.log("usedMem: ", usedMem)
        console.log("allocMem: ", allocMem)

        let memUsage = null;
        let memDisplay = "-";

        if (usedMem && allocMem) {
          const usedMiB = parseInt(usedMem.replace("Ki", ""), 10) / 1024;
          const allocMiB = parseInt(allocMem.replace("Ki", ""), 10) / 1024;

          const usagePercent = allocMiB > 0 ? (usedMiB / allocMiB) * 100 : 0;

          memUsage = usagePercent.toFixed(0);
          memDisplay = `${usedMiB.toFixed(1)}Mi / ${allocMiB.toFixed(1)}Mi`;
        }
        console.log("memUsage: ", memUsage)

        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Progress
              percent={memUsage ? parseFloat(memUsage) : 0}
              size="small"
              strokeColor={gradientStrokeColor}
              status="active"
              showInfo
              format={p => `${p}%`}
              strokeWidth={15}
            />
            <span style={{ fontSize: "11px", color: "#A8B0B9" }}>{memDisplay}</span>
          </div>
        );
      }
    },
    {
      title: 'Savings',
      dataIndex: 'savings',
      key: 'savings',
      align: 'center',
      render: (value) => `$${value}/mo`,
    },
    {
      title: '',
      key: 'action',
      render: () => (
        <div style={{ textAlign: 'right' }}>
          <AntdTooltip placement='topLeft' title="currently unavailable">
          <Button type="primary" size="small" style={{opacity:"0.5", cursor:"not-allowed"}}>Fix now</Button>
          </AntdTooltip>
        </div>
      ),
    }
  ];

  const parentColumns = [
    {
      dataIndex: 'title',
      key: 'title',
      render: (_, record) => {
        let valueToShow = "";

        if (record.title === "Resize overprovisioned workloads") {
          valueToShow = "Lower costs by scaling down overprovisioned containers based on historical usage trends";
        } else if (record.title === "Delete Unused PVCs") {
          valueToShow = "Identify and remove unattached Persistent Volume Claims (PVCs) to free up storage and cut storage costs";
        } else if (record.title === "Remove idle nodes (no pods scheduled)") {
          valueToShow = "Detect and decommission idle nodes with no active workloads to reduce compute overhead";
        }

        return (
          <div>
            <Text strong>{record.title}</Text>{' '}

            <div>
              <Text type="secondary">{valueToShow}</Text>
            </div>
          </div>
        );
      },
    },
    {
      key: 'savings',
      align: 'right',
      render: (_, record) => {
        const isExpanded = expandedKeys.includes(record.key);
        let valueToShow = "";
        if (record.title === "Resize overprovisioned workloads") {
          valueToShow = "";
        } else if (record.title === "Delete Unused PVCs") {
          const pvcTotal = unusedPVCs?.reduce((acc, pvc) => acc + (pvc.cost_usd || 0), 0);
          valueToShow = `$${pvcTotal?.toFixed(2)}/mo`;
        } else if (record.title === "Remove idle nodes (no pods scheduled)") {
          // const nodeTotal = idealNodes?.reduce((acc, node) => acc + (node.hourly_cost_usd?.[0] || 0), 0); // 24*30 = monthly
          // valueToShow = `$${nodeTotal?.toFixed(2)}/mo`;
          valueToShow = "";
        }
        return (
          <div>
            <Text strong className={styles.green}>
              {valueToShow}
            </Text>
            <Button
              type="link"
              size="small"
              onClick={() => {
                setExpandedKeys(isExpanded ? [] : [record.key]);
              }}
            >
              {isExpanded ? 'View less ' : 'View more '}
              <span style={{ fontWeight: '800' }}>{isExpanded ? '˄' : '˅'}</span>
            </Button>
          </div>
        );
      },
    },
  ];

  const calculatePotentialSavings = () => {
    if (wastedResource && typeof totalCost === "number") {
      const cpuWaste = wastedResource.totals.wasted_cpu_percent || 0;
      const memWaste = wastedResource.totals.wasted_memory_percent || 0;
      const avgWaste = (cpuWaste + memWaste) / 2;
      return ((avgWaste / 100) * totalCost).toFixed(2);
    }
    return null;
  };

  const potentialSaving = calculatePotentialSavings();


  return (

    <div className={styles.costOptimizationWrapper}>
      {(loadingSpend || loadingWasted) && (
        <div className={styles.loaderOverlay}>
          <Spin size="large" />
        </div>
      )}

      <Row gutter={[16, 16]} className={styles.summaryRow}>
        <Col span={5}>
          <Card className={styles.metricCard}>
            <Text className={styles.label}>CURRENT SPEND</Text>
            <div className={styles.metricTextGroup} style={{...(overviewData?.data?.cluster_data?.provisioner === 'imported' && { display: 'block' })}}>
              {costComponents && Object.keys(costComponents).length > 0 ? (
                <AntdTooltip
                  placement='topLeft'
                  title={
                    <div>
                      {Object.entries(costComponents).map(([key, value]) => (
                        <div key={key}>
                          <strong>{key.replace(/_/g, ' ')}:</strong> ${Number(value).toLocaleString()}
                        </div>
                      ))}
                    </div>
                  }
                >
                  <span className={styles.positive} style={{ color: '#008F2F', fontSize: '22px', marginBottom: '0px' }}>
                    {summaryValue}
                  </span>
                </AntdTooltip>
              ) : (

                overviewData?.data?.cluster_data?.provisioner != 'imported' ? (
                  <>
                    <span className={styles.positive} style={{ color: '#008F2F', fontSize: '22px', marginBottom: '0px' }}>
                      {summaryValue}
                    </span>
                    <img src={dollarImg} alt="Dollar Icon" className={styles.metricIcon} />
                  </>
                ) : (
                  <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', height: 'fit-content', marginTop: "-15px"}}>
                    <img src={noDataImg} alt="No data" />
                    <span>No Data Found</span>
                  </div>
                )

              )}
            </div>
          </Card>
        </Col>

        <Col span={5}>
          <Card className={styles.metricCard}>
            <Text className={styles.label}>POTENTIAL SAVINGS</Text>
            <div className={styles.metricTextGroup} style={{...(overviewData?.data?.cluster_data?.provisioner === 'imported' && { display: 'block' })}}>
             { overviewData?.data?.cluster_data?.provisioner != 'imported' ? (
              <>
              <Title level={3} className={styles.positive} style={{ color: '#FF7BAC', fontSize: '22px', marginBottom: '0px' }}>{potentialSaving ? `$${Number(potentialSaving).toLocaleString()}/mo` : 'Loading...'}</Title>
              <img src={piggyImg} alt="Piggy" className={styles.metricIcon} />
              </>
             ):(
                <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', height: 'fit-content', marginTop: "-15px"}}>
                    <img src={noDataImg} alt="No data" />
                    <span>No Data Found</span>
                  </div>
             )
             }
            </div>
          </Card>
        </Col>
        <Col span={5}>
          <Card className={styles.metricCard}>
            <Text className={styles.label}>EFFICIENCY SCORE</Text>
            <div className={styles.metricTextGroup} style={{...(overviewData?.data?.cluster_data?.provisioner === 'imported' && { display: 'block' })}}>
               { overviewData?.data?.cluster_data?.provisioner != 'imported' ? (
              <>
              <Title level={3} className={styles.positive} style={{ color: '#17ACE8', fontSize: '22px', marginBottom: '0px' }}>{efficiency.toFixed(0)}%</Title>
              <img src={speedImg} alt="Speed" className={styles.metricIcon} />
              </>
               ):(
                  <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', height: 'fit-content', marginTop: "-15px"}}>
                    <img src={noDataImg} alt="No data" />
                    <span>No Data Found</span>
                  </div>
               )}
            </div>
          </Card>
        </Col>
        <Col span={9}>
          <Card className={styles.metricCard}>
            <Text className={styles.label}>WASTED RESOURCES</Text>
            <div className={styles.metricTextGroup}>
              {console.log("wasted resource: ", wastedResource)}
              {isPrometheusMissing ? (
                <div style={{ marginTop: "-20px" }}>
                  <Text type="warning" style={{ fontWeight: '500', color: '#faad14' }}>
                    ⚠️ Wasted Resources data unavailable
                  </Text>
                  <br />
                  <Text type="secondary">
                    No Prometheus or metrics source detected. Connect a time-series database to generate  Wasted Resources data.
                  </Text>
                </div>
              ) : (
                wastedResource && (
                  <>
                    <Text className={styles.wasted}>
                      CPU – <span className={styles.orange}>{`${wastedResource.totals.wasted_cpu_percent}% Unused`}</span>
                    </Text>
                    <Text className={styles.wasted}>
                      RAM – <span className={styles.red}>{`${wastedResource.totals.wasted_memory_percent}% Unused`}</span>
                    </Text>
                  </>

                )
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <div className={styles.opportunitySection}>
        <Title level={5} >Optimization Opportunities</Title>
        <Text type="secondary" style={{ marginBottom: '14px' }}>Areas across your cluster where you can reduce costs by rightsizing workloads</Text>
        <Table
          columns={parentColumns}
          dataSource={optimizationGroups}
          expandable={{
            expandedRowRender: (record) => {
              let columnsToRender = [];

              if (record.title === "Delete Unused PVCs") {
                columnsToRender = [
                  {
                    title: 'Name',
                    dataIndex: 'name',
                    key: 'name',
                    width: 250,
                  },
                  {
                    title: 'Size (Gi)',
                    dataIndex: 'memory',
                    key: 'memory',
                    width: 200,
                    align: 'center',
                    render: (value) => `${value} Gi`
                  },
                  {
                    title: 'Cost',
                    dataIndex: 'savings',
                    key: 'savings',
                    align: 'center',
                    render: (value) => `$${value}/mo`,
                  },
                  {
                    title: '',
                    key: 'action',
                    render: () => (
                      <div style={{ textAlign: 'right' }}>
                        <AntdTooltip placement='topLeft' title="currently unavailable">
                        <Button type="primary" size="small" style={{opacity:"0.5", cursor:"not-allowed"}}>Fix now</Button>
                        </AntdTooltip>
                      </div>
                    ),
                  },
                ];
              } else if (record.title === "Remove idle nodes (no pods scheduled)") {
                columnsToRender = [
                  {
                    title: 'Name',
                    dataIndex: 'name',
                    key: 'name',
                    width: 250,
                  },
                  {
                    title: 'CPU',
                    dataIndex: 'cpu',
                    key: 'cpu',
                    width: 200,
                    render: (_, row) => {
                      const usedCPU = row.cpuUsage || "";
                      const allocatableCPU = row.allocatablecpu || "";

                      let cpuUsage = null;
                      let usageValueDisplay = "-";

                      if (usedCPU && allocatableCPU) {
                        let usedCores = 0;

                        if (usedCPU.includes("n")) {
                          usedCores = parseInt(usedCPU.replace("n", ""), 10) / 1e9;
                        } else if (usedCPU.includes("m")) {
                          usedCores = parseInt(usedCPU.replace("m", ""), 10) / 1000;
                        } else {
                          usedCores = parseFloat(usedCPU) || 0;
                        }

                        const allocatableCores = parseInt(allocatableCPU.replace("m", ""), 10) / 1000;
                        const usagePercent = allocatableCores > 0 ? (usedCores / allocatableCores) * 100 : 0;

                        cpuUsage = usagePercent.toFixed(0);
                        usageValueDisplay = `${usedCores.toFixed(1)}C / ${allocatableCores.toFixed(1)}C`;
                      }

                      return (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <Progress
                            percent={cpuUsage ? parseFloat(cpuUsage) : 0}
                            size="small"
                            strokeColor={gradientStrokeColor}
                            status="active"
                            showInfo
                            format={p => `${p}%`}
                            strokeWidth={15}
                          />
                          <span style={{ fontSize: "11px", color: "#A8B0B9" }}>{usageValueDisplay}</span>
                        </div>
                      );
                    }
                  },
                  {
                    title: 'Memory',
                    dataIndex: 'memory',
                    key: 'memory',
                    width: 200,
                    render: (_, row) => {
                      const usedMem = row.memoryUsage || "";
                      const allocMem = row.allocatablememory || "";

                      let memUsage = null;
                      let memDisplay = "-";

                      if (usedMem && allocMem) {
                        const usedMiB = parseInt(usedMem.replace("Ki", ""), 10) / 1024;
                        const allocMiB = parseInt(allocMem.replace("Ki", ""), 10) / 1024;

                        const usagePercent = allocMiB > 0 ? (usedMiB / allocMiB) * 100 : 0;

                        memUsage = usagePercent.toFixed(0);
                        memDisplay = `${usedMiB.toFixed(1)}Mi / ${allocMiB.toFixed(1)}Mi`;
                      }

                      return (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <Progress
                            percent={memUsage ? parseFloat(memUsage) : 0}
                            size="small"
                            strokeColor={gradientStrokeColor}
                            status="active"
                            showInfo
                            format={p => `${p}%`}
                            strokeWidth={15}
                          />
                          <span style={{ fontSize: "11px", color: "#A8B0B9" }}>{memDisplay}</span>
                        </div>
                      );
                    }
                  },
                  {
                    title: 'Cost',
                    dataIndex: 'savings',
                    key: 'savings',
                    align: 'center',
                    render: (value) => `$${value}/mo`,
                  },
                  {
                    title: '',
                    key: 'action',
                    render: () => (
                      <div style={{ textAlign: 'right' }}>
                        <AntdTooltip placement='topLeft' title="currently unavailable">
                        <Button type="primary" size="small" style={{opacity:"0.5", cursor:"not-allowed"}}>Fix now</Button>
                        </AntdTooltip>
                      </div>
                    ),
                  },
                ];
              }
              else if (record.title === 'Resize overprovisioned workloads') {
                if (isPrometheusMissing) {
                  return (
                    <div style={{ padding: '20px' }}>
                      <Text type="warning" style={{ fontWeight: '500', color: '#faad14' }}>
                        ⚠️ Recommendations unavailable
                      </Text>
                      <br />
                      <Text type="secondary">
                        No Prometheus or metrics source detected. Connect a time-series database to generate CPU and memory right-sizing suggestions.
                      </Text>
                    </div>
                  );
                }
                columnsToRender = [
                  {
                    title: 'Pod Name',
                    dataIndex: 'pod_name',
                    key: 'pod_name',
                    render: (_, row) => row.pod_name,
                  },
                  {
                    title: 'Containers',
                    key: 'containers',
                    render: (_, row) => (
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {row.containers.map((ctr, i) => (
                          <div
                            key={i}
                            style={{
                              borderBottom: row.containers.length > 1 && i !== row.containers.length - 1 ? '1px solid #eee' : 'none',
                              padding: '6px 0',
                              minHeight: '40px'
                            }}
                          >
                            {ctr.container}
                          </div>
                        ))}
                      </div>
                    )
                  },
                  {
                    title: 'CPU',
                    key: 'cpu',
                    render: (_, row) => (
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {row.containers.map((ctr, i) => (
                          <div
                            key={i}
                            style={{
                              borderBottom: row.containers.length > 1 && i !== row.containers.length - 1 ? '1px solid #eee' : 'none',
                              padding: '6px 0',
                              minHeight: '40px', // Ensure all rows align equally
                            }}
                          >
                            <span style={{ marginRight: "2px" }}>Curr : {ctr.cpu.current}m</span> →
                            <span style={{ marginLeft: "2px" }}>Rec : {ctr.cpu.recommended}m</span>
                          </div>
                        ))}
                      </div>
                    )
                  },
                  {
                    title: 'RAM',
                    key: 'memory',
                    render: (_, row) => (
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {row.containers.map((ctr, i) => (
                          <div
                            key={i}
                            style={{
                              borderBottom: row.containers.length > 1 && i !== row.containers.length - 1 ? '1px solid #eee' : 'none',
                              padding: '6px 0',
                              minHeight: '40px'
                            }}
                          >
                            <span style={{ marginRight: "2px" }}>Curr : {ctr.memory.current}Mi</span> →
                            <span  style={{ marginLeft: "2px" }}>Rec : {ctr.memory.recommended}Mi</span>
                          </div>
                        ))}
                      </div>
                    )
                  },
                  //  {
                  //   title: 'Risk',
                  //   key: 'risk',
                  //   render: (_, row) => (
                  //     <div style={{ display: 'flex', flexDirection: 'column' }}>
                  //       {row.containers.map((ctr, i) => (
                  //         <div
                  //           key={i}
                  //           style={{
                  //             borderBottom: row.containers.length > 1 && i !== row.containers.length - 1 ? '1px solid #eee' : 'none',
                  //             padding: '6px 0',
                  //             minHeight: '40px'
                  //           }}
                  //         >
                  //           {ctr.risk}
                  //         </div>
                  //       ))}
                  //     </div>
                  //   )
                  // },
                  {
                    title: '',
                    key: 'action',
                    render: (_, row) => (
                      <>
                        {row.containers.map((ctr, i) => (
                          <div
                            key={i}
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              borderBottom:
                                row.containers.length > 1 && i !== row.containers.length - 1
                                  ? '1px solid #e5e5e5'
                                  : 'none',
                              padding: '6px 0',
                              minHeight: row.containers.length > 2 ? '48px' : '36px',
                            }}
                          >
                            <AntdTooltip placement='topLeft' title="currently unavailable">
                            <Button type="primary" size="small" style={{opacity:"0.5", cursor:"not-allowed"}}>Fix now</Button>
                            </AntdTooltip>
                          </div>
                        ))}
                      </>
                    ),
                  },
                ];
              }
              else {
                columnsToRender = nestedColumns;
              }

              let isLoading = false;

              if (record.title === "Resize overprovisioned workloads") {
                isLoading = isResizingLoading;
              } else if (record.title === "Delete Unused PVCs") {
                isLoading = isPVCsLoading;
              } else if (record.title === "Remove idle nodes (no pods scheduled)") {
                isLoading = isIdleNodesLoading;
              }
              return (
                <Table
                  columns={columnsToRender}
                  dataSource={record.workloads.map((w, i) => ({ ...w, key: i }))}
                  pagination={false}
                  loading={isLoading}
                />
              );
            },
            expandedRowKeys: expandedKeys,
            onExpand: (expanded, record) => setExpandedKeys(expanded ? [record.key] : []),
            expandIcon: () => null,
            expandIconColumnIndex: -1,
          }}
          pagination={false}
          showHeader={false}
          rowClassName={styles.optimizationRow}
        />
      </div>

      <Card className={styles.trendCard}>
        <Title level={5}>Cost Trend</Title>
        <Text type="secondary">
          How your cluster’s monthly spend changes over time to help track savings and forecast future costs.
        </Text>

        <ResponsiveContainer width="100%" height={300}>
          {isPrometheusMissing ? (
            <div style={{ padding: '20px' }}>
              <Text type="warning" style={{ fontWeight: '500', color: '#faad14' }}>
                ⚠️ Cost Trends unavailable
              </Text>
              <br />
              <Text type="secondary">
                No Prometheus or metrics source detected. Connect a time-series database to generate Cost Trends.
              </Text>
            </div>
          ) : (
            <AreaChart data={costTrendData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4096ff" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#4096ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" stroke="#999" />
              <YAxis stroke="#999" domain={[0, 20]} />
              <CartesianGrid strokeDasharray="3 3" />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#4096ff"
                fillOpacity={1}
                fill="url(#colorValue)"
                strokeWidth={2}
              />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </Card>
    </div>
  );
}